import {
  MarkdownAdapter,
  titleMiddleware,
  embedSyncedDocMiddleware,
} from '@blocksuite/affine/shared/adapters';
import type { EditorHost } from '@blocksuite/affine/std';
import type {
  BlockModel,
  BlockSnapshot,
  DocSnapshot,
  Store,
} from '@blocksuite/affine/store';
import type { Doc } from '../../doc/entities/doc';
import {
  convertSingleBlockToMarkdown,
  getAllBlocks,
} from '../utils/chat-ops-parse';

export interface BlockLineNumberInfo {
  startLineNumber: number;
  endLineNumber: number;
  blockIds: string[];
  markdownContent: string;
  confidence: 'high' | 'medium' | 'low';
}

export class BlockMarkdownLineNumberResolver {
  private readonly host: EditorHost;
  private readonly store: Store;
  private readonly doc: Doc;

  constructor(host: EditorHost, doc: Doc) {
    this.host = host;
    this.store = host.std.store;
    this.doc = doc;
  }

  /**
   * 主要方法：获取选中blocks在全文markdown中的行号
   * 优化版本：提升性能、可读性和可维护性
   */
  async getSelectedBlocksLineNumber(
    selectedBlockIds: string[]
  ): Promise<BlockLineNumberInfo | null> {
    try {
      // 1. 早期验证：避免不必要的计算
      if (selectedBlockIds.length === 0) {
        return null;
      }

      // 2. 批量获取blocks，减少重复查询
      const selectedBlocks = this._getValidBlocks(selectedBlockIds);

      if (selectedBlocks.length === 0) {
        return null;
      }

      // 3. 并行获取必要数据，提升性能
      const [selectedMarkdowns, documentData] = await Promise.all([
        this._generateSelectedMarkdowns(selectedBlocks),
        this._getAllBlocksInOrder(),
      ]);

      // 4. 验证数据完整性
      if (!documentData || selectedMarkdowns.length === 0) {
        return null;
      }

      // 5. 获取所有blocks（缓存优化）
      const allBlocks = this._getAllBlocksCached();

      // 6. 查找行号位置
      const result = await this._findMarkdownPositionInDocument(
        selectedMarkdowns,
        documentData.markdownContent,
        selectedBlocks,
        allBlocks
      );

      return result;
    } catch (error) {
      console.error('Failed to get selected blocks line number:', error);
      return null;
    }
  }

  /**
   * 🔧 辅助方法：获取有效的blocks
   * 优化：减少类型转换和过滤操作
   */
  private _getValidBlocks(selectedBlockIds: string[]): BlockModel[] {
    const blocks: BlockModel[] = [];

    for (const id of selectedBlockIds) {
      const block = this.store.getBlock(id);
      if (block?.model && this.isContentBlock(block)) {
        blocks.push(block.model);
      }
    }

    return blocks;
  }

  /**
   * 🔧 辅助方法：生成选中blocks的markdown
   * 优化：只处理首尾blocks，避免处理所有中间blocks
   */
  private async _generateSelectedMarkdowns(
    selectedBlocks: BlockModel[]
  ): Promise<string[]> {
    // 只处理第一个和最后一个block（如果不同）
    const firstBlock = selectedBlocks[0];
    const lastBlock = selectedBlocks[selectedBlocks.length - 1];

    try {
      // 并行生成markdown
      const promises: Promise<string>[] = [];

      if (firstBlock) {
        promises.push(convertSingleBlockToMarkdown(this.host, firstBlock.id));
      }

      // 只有当最后一个block与第一个不同时才处理
      if (lastBlock && lastBlock !== firstBlock) {
        promises.push(convertSingleBlockToMarkdown(this.host, lastBlock.id));
      }

      const results = await Promise.all(promises);
      return results.filter(Boolean); // 过滤掉空值
    } catch (error) {
      console.error('Failed to generate selected markdowns:', error);
      return [];
    }
  }

  /**
   * 🔑 获取 block 的 role
   */
  private getBlockRole(block: any): string | undefined {
    // 方法1: 通过 model.role 获取
    if (block.model?.role) {
      return block.model.role;
    }

    // 方法2: 通过 schema.model.role 获取
    if (block.model?.schema?.model?.role) {
      return block.model.schema.model.role;
    }

    return undefined;
  }

  /**
   * 🔑 判断块是否为特定角色
   */
  private isBlockRole(block: any, targetRole: string): boolean {
    const role = this.getBlockRole(block);
    return role === targetRole;
  }

  /**
   * 🔑 判断是否为根块
   */
  private isRootBlock(block: any): boolean {
    return this.isBlockRole(block, 'root');
  }

  /**
   * 🔑 判断是否为内容块
   */
  private isContentBlock(block: any): boolean {
    return this.isBlockRole(block, 'content');
  }

  /**
   * 🔑 判断是否为容器块（hub）
   */
  private isHubBlock(block: any): boolean {
    return this.isBlockRole(block, 'hub');
  }

  /**
   * 🔧 辅助方法：缓存优化的getAllBlocks
   * 优化：避免重复调用getAllBlocks
   */
  private _allBlocksCache: BlockModel[] | null = null;
  private _cacheTimestamp = 0;
  private readonly _cacheTimeout = 5000; // 5秒缓存

  private _getAllBlocksCached(): BlockModel[] {
    const now = Date.now();

    // 检查缓存是否有效
    if (
      this._allBlocksCache &&
      now - this._cacheTimestamp < this._cacheTimeout
    ) {
      return this._allBlocksCache;
    }

    // 更新缓存
    this._allBlocksCache = getAllBlocks(this.doc.blockSuiteDoc);
    this._cacheTimestamp = now;

    return this._allBlocksCache;
  }

  // 根据 selectedMarkdown 和  start_line_number 查询到对应的 偏移量
  async getSelectedMarkdownMappingBlock(
    selectedMarkdown: string,
    start_line_number: number
  ): Promise<{
    matches: Array<{
      startLine: number;
      endLine: number;
      content: string;
    }>;
    offsetIndex: number;
  } | null> {
    const result = await this._getAllBlocksInOrder();
    if (!result) {
      return null;
    }
    const allBlocks: BlockSnapshot[] =
      result?.snapshot.blocks.children[1].children || [];

    const fullDocumentMarkdown = result?.markdownContent || '';

    // 1. 直接字符串匹配（高置信度）
    const directMatchResult = this._findDirectStringMatch(
      selectedMarkdown,
      fullDocumentMarkdown
    );

    if (directMatchResult && directMatchResult.matches.length) {
      const offsetIndex = directMatchResult.matches.findIndex(
        match => match.startLine === Number(start_line_number)
      );

      return {
        matches: directMatchResult.matches,
        offsetIndex,
      };
    }

    return null;
  }

  /**
   * 在全文markdown中查找选中片段的位置
   */
  private async _findMarkdownPositionInDocument(
    selectedMarkdowns: string[],
    fullDocumentMarkdown: string,
    selectedBlocks: BlockModel[],
    allBlocks: BlockModel[]
  ): Promise<BlockLineNumberInfo> {
    try {
      // 1. 直接字符串匹配（高置信度）
      const directMatchResults = selectedMarkdowns.map(m =>
        this._findDirectStringMatch(m, fullDocumentMarkdown)
      );

      const [firstMarkdown, lastMarkdown] = selectedMarkdowns;

      // 检查是否有有效的匹配结果
      const validResults = directMatchResults.filter(result => result !== null);

      if (validResults.length > 0) {
        const [firstMatch, lastMatch] = [
          validResults[0],
          validResults[validResults.length - 1],
        ];
        let startLineNumber = 1,
          endLineNumber = 1;

        if (firstMatch?.matches.length === 1) {
          startLineNumber = firstMatch.matches[0].startLine;
        }
        if (lastMatch?.matches.length === 1) {
          endLineNumber = lastMatch.matches[0].endLine;
        }

        // 如果第一个和最后一个都只有一个匹配，直接返回高置信度结果
        if (
          firstMatch?.matches.length === 1 &&
          lastMatch?.matches.length === 1
        ) {
          return {
            startLineNumber,
            endLineNumber,
            blockIds: selectedBlocks.map(b => b.id),
            markdownContent: firstMarkdown,
            confidence: 'high',
          };
        }
      }

      // 2. 如果直接匹配有多个结果，使用block ID映射方法
      const firstValidResult = validResults[0];
      if (firstValidResult && firstValidResult.matches.length > 1) {
        const [firstOffsetIndex, lastOffsetIndex] = await Promise.all([
          this._findPositionByBlockIdMapping(
            selectedBlocks,
            firstMarkdown,
            allBlocks
          ),
          this._findPositionByBlockIdMapping(
            selectedBlocks,
            lastMarkdown,
            allBlocks
          ),
        ]);

        const firstMatche = firstValidResult.matches[firstOffsetIndex];
        const lastMatche = firstValidResult.matches[lastOffsetIndex];

        if (firstMatche && lastMatche) {
          return {
            startLineNumber: firstMatche.startLine,
            endLineNumber: lastMatche.endLine,
            blockIds: selectedBlocks.map(b => b.id),
            markdownContent: firstMatche.content,
            confidence: 'medium',
          };
        }
      }

      // 3. Fallback：未匹配到，返回低置信度且行号为 1
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: firstMarkdown,
        confidence: 'low',
      };
    } catch (error) {
      console.error('Failed to find markdown position in document:', error);
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdowns[0] || '',
        confidence: 'low',
      };
    }
  }

  /**
   * 直接字符串匹配
   */
  private _findDirectStringMatch(
    selectedMarkdown: string,
    fullDocumentMarkdown: string
  ): {
    matches: Array<{ startLine: number; endLine: number; content: string }>;
  } | null {
    try {
      const fullLines = fullDocumentMarkdown.split('\n');
      const selectedLines = selectedMarkdown.split('\n');
      const matches: Array<{
        startLine: number;
        endLine: number;
        content: string;
      }> = [];

      // 在全文的每一行开始位置尝试匹配
      for (let i = 0; i <= fullLines.length - selectedLines.length; i++) {
        let match = true;

        // 检查从第i行开始的连续行是否匹配
        for (let j = 0; j < selectedLines.length; j++) {
          if (!fullLines[i + j].trim().startsWith(selectedLines[j].trim())) {
            match = false;
            break;
          }
        }

        if (match) {
          matches.push({
            startLine: i + 1, // 转换为1-based行号
            endLine: i + selectedLines.length,
            content: selectedLines.join('\n'),
          });
        }
      }

      return { matches };
    } catch (error) {
      console.error('Failed to find direct string match:', error);
      return null;
    }
  }

  /**
   * 通过block ID映射查找位置
   */
  private async _findPositionByBlockIdMapping(
    selectedBlocks: BlockModel[],
    selectedMarkdown: string,
    allBlocks: BlockModel[]
  ): Promise<number> {
    let sameMDBlocks = [];

    const selectedBlock = selectedBlocks[0];

    for (const block of allBlocks) {
      if (block.flavour === selectedBlock.flavour) {
        const blockContent = await convertSingleBlockToMarkdown(
          this.host,
          block.id
        );
        if (blockContent.trim() === selectedMarkdown.trim()) {
          sameMDBlocks.push(block);
        }
      }
    }

    return sameMDBlocks.findIndex(block => selectedBlock.id === block.id);
  }

  /**
   * 模糊匹配方法
   */
  private _findPositionByFuzzyMatching(
    selectedMarkdown: string,
    fullDocumentMarkdown: string,
    selectedBlocks: BlockModel[]
  ): BlockLineNumberInfo {
    try {
      // 使用相似度算法找到最匹配的位置
      const bestMatch = this._findBestFuzzyMatch(
        selectedMarkdown,
        fullDocumentMarkdown
      );

      return {
        startLineNumber: bestMatch.startLine,
        endLineNumber: bestMatch.endLine,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    } catch (error) {
      console.error('Failed to find position by fuzzy matching:', error);
      // 返回默认值
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    }
  }

  /**
   * 模糊匹配算法
   */
  private _findBestFuzzyMatch(
    selectedMarkdown: string,
    fullDocumentMarkdown: string
  ): { startLine: number; endLine: number } {
    try {
      const fullLines = fullDocumentMarkdown.split('\n');
      const selectedLines = selectedMarkdown.split('\n');

      let bestMatch = { startLine: 1, endLine: 1, score: 0 };

      // 滑动窗口查找最佳匹配
      for (let i = 0; i <= fullLines.length - selectedLines.length; i++) {
        let score = 0;

        for (let j = 0; j < selectedLines.length; j++) {
          if (fullLines[i + j] === selectedLines[j]) {
            score += 1;
          }
        }

        const matchScore = score / selectedLines.length;
        if (matchScore > bestMatch.score) {
          bestMatch = {
            startLine: i + 1,
            endLine: i + selectedLines.length,
            score: matchScore,
          };
        }
      }

      return {
        startLine: bestMatch.startLine,
        endLine: bestMatch.endLine,
      };
    } catch (error) {
      console.error('Failed to find best fuzzy match:', error);
      return { startLine: 1, endLine: 1 };
    }
  }

  /**
   * 获取所有blocks的有序列表
   */
  private async _getAllBlocksInOrder(): Promise<{
    markdownContent: string;
    snapshot: DocSnapshot;
  } | null> {
    const blockSuiteDoc = this.doc?.blockSuiteDoc;
    if (!blockSuiteDoc) {
      return null;
    }

    // Create transformer for document conversion
    const transformer = blockSuiteDoc.getTransformer([
      titleMiddleware(blockSuiteDoc.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ]);

    const snapshot = transformer.docToSnapshot(blockSuiteDoc);

    if (snapshot) {
      const adapter = new MarkdownAdapter(transformer, blockSuiteDoc.provider);
      const markdownResult = await adapter.fromDocSnapshot({
        snapshot,
        assets: transformer.assetsManager,
      });

      return {
        markdownContent: markdownResult.file,
        snapshot: snapshot,
      };
    }

    return null;
  }
}
